import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getPaymentStatus, type PaymentStatusResponse } from '../api/alipayClient';

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 20;

type ResultStatus = 'loading' | 'success' | 'failed' | 'refunded' | 'pending' | 'error';

const primaryButton =
    'px-6 py-3 bg-ai-primary text-black rounded-xl font-medium hover:bg-ai-primary/90 transition-all focus:outline-none focus-visible:ring focus-visible:ring-ai-focus';
const secondaryButton =
    'px-6 py-3 bg-ai-surface-glow/20 text-ai-text-primary rounded-xl font-medium hover:bg-ai-surface-glow/40 transition-all focus:outline-none focus-visible:ring focus-visible:ring-ai-focus';

/**
 * 支付结果页面
 *
 * 处理支付宝同步返回的 return_url 跳转。
 * URL 参数示例: /payment/result?out_trade_no=PS20260410...
 *
 * 轮询时带 sync=true，让后端在异步通知未到达时主动向支付宝查单；
 * 超时仍未确认时不判定失败（点数会由后端补偿到账），提供「重新检查」。
 */
export function PaymentResultPage() {
    const { t } = useTranslation();
    const [orderNo] = useState(() => new URLSearchParams(window.location.search).get('out_trade_no'));
    const [status, setStatus] = useState<ResultStatus>(orderNo ? 'loading' : 'error');
    const [orderInfo, setOrderInfo] = useState<PaymentStatusResponse | null>(null);
    const [errorMessage, setErrorMessage] = useState(orderNo ? '' : t('payment.result.missingOrder'));
    const [pollRound, setPollRound] = useState(0);

    const applyResult = useCallback((result: PaymentStatusResponse) => {
        setOrderInfo(result);
        if (result.status === 'PAID' && result.recharged) {
            setStatus('success');
            return true;
        }
        if (result.status === 'REFUNDED') {
            setStatus('refunded');
            return true;
        }
        if (result.status === 'FAILED' || result.status === 'CLOSED') {
            setStatus('failed');
            return true;
        }
        // PENDING，或已支付但点数尚未到账
        return false;
    }, []);

    useEffect(() => {
        if (!orderNo) return;

        let attempts = 0;
        let anySucceeded = false;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const poll = async () => {
            attempts++;
            let done = false;
            try {
                const result = await getPaymentStatus(orderNo, true);
                anySucceeded = true;
                done = !cancelled && applyResult(result);
            } catch (error) {
                console.warn('[payment] status check failed', error);
            }
            if (cancelled || done) return;
            if (attempts < MAX_ATTEMPTS) {
                timer = setTimeout(poll, POLL_INTERVAL_MS);
            } else if (anySucceeded) {
                setStatus('pending');
            } else {
                setStatus('error');
                setErrorMessage(t('payment.result.queryFailed'));
            }
        };

        setStatus('loading');
        poll();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [orderNo, pollRound, applyResult, t]);

    const handleCheckAgain = () => setPollRound((round) => round + 1);

    const handleGoHome = () => {
        window.location.href = '/';
    };

    const handleRepay = () => {
        window.location.href = '/?pricing=1';
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-ai-bg px-4 py-10">
            <div
                className="w-full max-w-md rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/95 shadow-ai-card p-8 text-center"
                role="status"
                aria-live="polite"
            >
                {status === 'loading' && (
                    <>
                        <div className="text-6xl mb-4 animate-pulse" aria-hidden="true">⏳</div>
                        <h2 className="text-xl font-semibold text-ai-text-primary mb-2">
                            {t('payment.waitingForPayment')}
                        </h2>
                        <p className="text-ai-text-secondary text-sm">
                            {t('payment.processing')}
                        </p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="text-6xl mb-4" aria-hidden="true">✅</div>
                        <h2 className="text-xl font-semibold text-ai-text-primary mb-2">
                            {t('payment.success')}
                        </h2>
                        <p className="text-ai-text-secondary mb-6">
                            {t('payment.successDesc', { points: orderInfo?.points || 0 })}
                        </p>
                        <button type="button" onClick={handleGoHome} className={primaryButton}>
                            {t('payment.result.backHome')}
                        </button>
                    </>
                )}

                {status === 'failed' && (
                    <>
                        <div className="text-6xl mb-4" aria-hidden="true">❌</div>
                        <h2 className="text-xl font-semibold text-ai-text-primary mb-2">
                            {t('payment.failed')}
                        </h2>
                        <p className="text-ai-text-secondary mb-6">
                            {t('payment.failedDesc')}
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                            <button type="button" onClick={handleRepay} className={primaryButton}>
                                {t('payment.retry')}
                            </button>
                            <button type="button" onClick={handleGoHome} className={secondaryButton}>
                                {t('payment.result.backHome')}
                            </button>
                        </div>
                    </>
                )}

                {status === 'refunded' && (
                    <>
                        <div className="text-6xl mb-4" aria-hidden="true">↩️</div>
                        <h2 className="text-xl font-semibold text-ai-text-primary mb-2">
                            {t('payment.result.refundedTitle')}
                        </h2>
                        <p className="text-ai-text-secondary mb-6">
                            {t('payment.result.refundedDesc', { points: orderInfo?.refundPoints ?? 0 })}
                        </p>
                        <button type="button" onClick={handleGoHome} className={secondaryButton}>
                            {t('payment.result.backHome')}
                        </button>
                    </>
                )}

                {status === 'pending' && (
                    <>
                        <div className="text-6xl mb-4" aria-hidden="true">🕒</div>
                        <h2 className="text-xl font-semibold text-ai-text-primary mb-2">
                            {t('payment.result.pendingTitle')}
                        </h2>
                        <p className="text-ai-text-secondary mb-6">
                            {t('payment.result.pendingDesc')}
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                            <button type="button" onClick={handleCheckAgain} className={primaryButton}>
                                {t('payment.result.checkAgain')}
                            </button>
                            <button type="button" onClick={handleGoHome} className={secondaryButton}>
                                {t('payment.result.backHome')}
                            </button>
                        </div>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="text-6xl mb-4" aria-hidden="true">⚠️</div>
                        <h2 className="text-xl font-semibold text-ai-text-primary mb-2">
                            {t('payment.result.errorTitle')}
                        </h2>
                        <p className="text-ai-text-secondary mb-6">
                            {errorMessage}
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                            {orderNo && (
                                <button type="button" onClick={handleCheckAgain} className={primaryButton}>
                                    {t('payment.result.checkAgain')}
                                </button>
                            )}
                            <button type="button" onClick={handleGoHome} className={secondaryButton}>
                                {t('payment.result.backHome')}
                            </button>
                        </div>
                    </>
                )}

                {orderNo && (
                    <p className="mt-6 text-xs text-ai-text-secondary/70 break-all">
                        {t('payment.result.orderNo', { orderNo })}
                    </p>
                )}
            </div>
        </div>
    );
}
